/**
 * The docked task pocket (spec §6.5): 12px from the right edge of the canvas
 * area in the menu material. Work
 * toasts, tray rows, notifications, search, history, the Tasks tab, memory,
 * conversation “Back to task”, /review and GitHub open it here.
 */
import { useEffect, useRef } from "react";
import { useInteractiveLayer } from "@/components/layout/interactive-layer";
import { pocketDockClassName } from "@/lib/task-pocket-placement";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useTasksStore } from "@/stores/tasks-store";
import { TaskPocket } from "./task-pocket";
import { useTaskPocketDismiss } from "./use-task-pocket-dismiss";

export function TaskPocketDock({
  projectId,
  visible,
  headerChrome,
}: {
  projectId: string | null;
  /** The canvas workspace shows (not the desktop compact mode). */
  visible: boolean;
  /** The web header sits above the canvas. */
  headerChrome: boolean;
}) {
  const taskId = useTaskPocketStore((s) =>
    s.anchor?.kind === "docked" ? s.taskId : null,
  );
  const hasTask = useTasksStore(
    (s) => taskId !== null && s.tasks.some((task) => task.id === taskId),
  );
  const previousProject = useRef(projectId);
  useEffect(() => {
    const changedProject = previousProject.current !== projectId;
    previousProject.current = projectId;
    const pocket = useTaskPocketStore.getState();
    if (pocket.taskId && (changedProject || !projectId))
      pocket.clearForProjectChange();
  }, [projectId]);
  const open = visible && projectId !== null && hasTask;
  const pocketRef = useRef<HTMLDivElement>(null);
  const layer = useInteractiveLayer("task-pocket", {
    active: open,
    baseZIndex: 20,
  });
  useTaskPocketDismiss(pocketRef, open);

  if (!open || !projectId || !taskId) return null;
  return (
    <div
      className={pocketDockClassName({ headerChrome })}
      style={layer.style}
      {...layer.interactionProps}
      data-testid="task-pocket-dock"
    >
      <TaskPocket
        key={taskId}
        ref={pocketRef}
        taskId={taskId}
        className="pointer-events-auto max-h-full"
      />
    </div>
  );
}
